// frontend/src/components/ui/enhanced-account-card.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils';
import {
    BuildingOfficeIcon,
    EnvelopeIcon,
    PhoneIcon,
    GlobeAltIcon,
    MapPinIcon,
    UserGroupIcon,
    CalendarIcon,
    TagIcon,
    EyeIcon,
    PencilIcon,
    TrashIcon
} from '@heroicons/react/24/outline';

// Enhanced Account Card Component
export const EnhancedAccountCard = ({
    account,
    onEdit,
    onDelete,
    onView,
    className = "",
    showActions = true,
    compact = false
}) => {
    // Calculate completion percentage based on filled fields
    const getCompletionPercentage = () => {
        const essentialFields = [
            account.name,
            account.primaryContact?.name,
            account.primaryContact?.email,
            account.address?.city,
            account.address?.country,
            account.website,
            account.industry
        ];
        
        const filledFields = essentialFields.filter(field => field && field.toString().trim() !== '').length;
        return Math.round((filledFields / essentialFields.length) * 100);
    };

    const completionPercentage = getCompletionPercentage();
    
    // Get status color
    const getStatusColor = (status) => {
        const colors = {
            active: 'bg-green-100 dark:bg-green-950/20 text-green-700 dark:text-green-300',
            prospect: 'bg-blue-100 dark:bg-blue-950/20 text-blue-700 dark:text-blue-300',
            inactive: 'bg-muted text-foreground',
            archived: 'bg-muted text-foreground'
        };
        return colors[status] || colors.prospect;
    };

    // Get lifecycle color
    const getLifecycleColor = (lifecycle) => {
        const colors = {
            lead: 'bg-yellow-100 dark:bg-yellow-950/20 text-yellow-700 dark:text-yellow-300',
            prospect: 'bg-blue-100 dark:bg-blue-950/20 text-blue-700 dark:text-blue-300',
            customer: 'bg-green-100 dark:bg-green-950/20 text-green-700 dark:text-green-300',
            churned: 'bg-red-100 dark:bg-red-950/20 text-red-700 dark:text-red-300'
        };
        return colors[lifecycle] || colors.lead;
    };

    // Format date
    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    // Get company initials
    const getInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase().slice(0, 2);
    };

    const CardContent = () => (
        <div className={cn(
            "bg-card rounded-xl shadow-sm border border-border p-6 hover:shadow-md transition-all duration-200",
            compact && "p-4",
            className
        )}>
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                    <div className="relative">
                        {account.logoUrl && account.logoUrl.trim() !== '' ? (
                            <img 
                                src={account.logoUrl} 
                                alt={`${account.name} logo`}
                                className={cn(
                                    "rounded-lg object-contain bg-card border-2 border-border shadow-sm",
                                    compact ? "h-10 w-10" : "h-12 w-12"
                                )}
                                crossOrigin="anonymous"
                                onError={(e) => {
                                    console.warn('Logo failed to load, showing fallback:', account.logoUrl);
                                    e.target.style.display = 'none';
                                    const fallback = e.target.nextElementSibling;
                                    if (fallback) {
                                        fallback.style.display = 'flex';
                                    }
                                }}
                                onLoad={() => console.log('Logo loaded successfully:', account.logoUrl)}
                            />
                        ) : null}
                        <div className={cn(
                            "rounded-lg flex items-center justify-center text-white font-bold bg-gradient-to-br from-purple-500 to-blue-600",
                            compact ? "h-10 w-10 text-sm" : "h-12 w-12 text-lg",
                            account.logoUrl && account.logoUrl.trim() !== '' ? 'hidden' : 'flex'
                        )}>
                            {getInitials(account.name)}
                        </div>
                    </div>
                    <div className="min-w-0 flex-1">
                        <h3 className={cn(
                            "font-semibold text-foreground line-clamp-1",
                            compact ? "text-sm" : "text-base"
                        )}>
                            {account.name || 'Unnamed Company'}
                        </h3>
                        {account.industry && (
                            <p className={cn(
                                "text-muted-foreground",
                                compact ? "text-xs" : "text-sm"
                            )}>
                                {account.industry}
                            </p>
                        )}
                    </div>
                </div>
                <div className="flex items-center space-x-2">
                    {account.lifecycle && (
                        <span className={cn(
                            "px-2 py-1 text-xs font-semibold rounded-full",
                            getLifecycleColor(account.lifecycle)
                        )}>
                            {account.lifecycle}
                        </span>
                    )}
                    {account.status && (
                        <span className={cn(
                            "px-2 py-1 text-xs font-semibold rounded-full",
                            getStatusColor(account.status)
                        )}>
                            {account.status}
                        </span>
                    )}
                </div>
            </div>

            {/* Essential Information */}
            <div className="space-y-3 mb-4">
                {/* Primary Contact */}
                {account.primaryContact && (
                    <div className="flex items-center space-x-3 text-sm">
                        <UserGroupIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                            <p className="text-foreground truncate">
                                {account.primaryContact.name || 'No contact name'}
                            </p>
                            {account.primaryContact.email && (
                                <p className="text-muted-foreground truncate">
                                    {account.primaryContact.email}
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {/* Contact Information */}
                <div className="grid grid-cols-1 gap-2">
                    {account.primaryContact?.phoneE164 && (
                        <div className="flex items-center space-x-3 text-sm">
                            <PhoneIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <span className="text-muted-foreground truncate">
                                {account.primaryContact.phoneE164}
                            </span>
                        </div>
                    )}
                    
                    {account.website && (
                        <div className="flex items-center space-x-3 text-sm">
                            <GlobeAltIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <a
                                href={account.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 truncate"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {account.website.replace(/^https?:\/\//, '')}
                            </a>
                        </div>
                    )}
                    
                    {account.address && (
                        <div className="flex items-center space-x-3 text-sm">
                            <MapPinIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <span className="text-muted-foreground truncate">
                                {[account.address.city, account.address.country]
                                    .filter(Boolean)
                                    .join(', ') || 'No location'}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Completion Progress */}
            <div className="mb-4">
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                    <span>Profile Completion</span>
                    <span>{completionPercentage}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-1.5">
                    <div
                        className={cn(
                            "h-1.5 rounded-full transition-all duration-300",
                            completionPercentage >= 80 ? "bg-green-500" :
                            completionPercentage >= 60 ? "bg-yellow-500" : "bg-red-500"
                        )}
                        style={{ width: `${completionPercentage}%` }}
                    />
                </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-4 border-t border-border">
                <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                    {/* Client Count */}
                    <div className="flex items-center space-x-1">
                        <UserGroupIcon className="h-4 w-4" />
                        <span>{account.clientCount || 0} clients</span>
                    </div>
                    
                    {/* Last Activity */}
                    {account.lastActivityAt && (
                        <div className="flex items-center space-x-1">
                            <CalendarIcon className="h-4 w-4" />
                            <span>{formatDate(account.lastActivityAt)}</span>
                        </div>
                    )}
                </div>

                {/* Actions */}
                {showActions && (
                    <div className="flex items-center space-x-2">
                        {onView && (
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    onView(account);
                                }}
                                className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"
                                title="View details"
                            >
                                <EyeIcon className="h-4 w-4" />
                            </button>
                        )}
                        {onEdit && (
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    onEdit(account);
                                }}
                                className="p-1.5 text-muted-foreground hover:text-blue-600 transition-colors"
                                title="Edit account"
                            >
                                <PencilIcon className="h-4 w-4" />
                            </button>
                        )}
                        {onDelete && (
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    onDelete(account);
                                }}
                                className="p-1.5 text-muted-foreground hover:text-red-600 transition-colors"
                                title="Delete account"
                            >
                                <TrashIcon className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Tags */}
            {account.tags && account.tags.length > 0 && (
                <div className="mt-3 pt-3 border-t border-border">
                    <div className="flex flex-wrap gap-1">
                        {account.tags.slice(0, 3).map((tag, index) => (
                            <span
                                key={index}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-muted text-foreground"
                            >
                                <TagIcon className="h-3 w-3 mr-1" />
                                {tag}
                            </span>
                        ))}
                        {account.tags.length > 3 && (
                            <span className="text-xs text-muted-foreground">
                                +{account.tags.length - 3} more
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );

    // If it's a link, wrap in Link component
    if (account._id && !onView) {
        return (
            <Link to={`/accounts/${account._id}`} className="block">
                <CardContent />
            </Link>
        );
    }

    return <CardContent />;
};

// Compact Account Card for lists
export const CompactAccountCard = ({ account, ...props }) => (
    <EnhancedAccountCard
        account={account}
        compact={true}
        {...props}
    />
);

// Account Card Grid
export const AccountCardGrid = ({ 
    accounts = [], 
    onEdit, 
    onDelete, 
    onView,
    className = "",
    emptyMessage = "No accounts found",
    showAddButton = true
}) => {
    if (accounts.length === 0) {
        return (
            <div className={cn(
                "text-center py-12",
                className
            )}>
                <BuildingOfficeIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">No accounts found</h3>
                <p className="text-muted-foreground mb-6">{emptyMessage}</p>
                {showAddButton && (
                    <Link
                        to="/accounts/new"
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring"
                    >
                        <BuildingOfficeIcon className="h-5 w-5 mr-2" />
                        Add Account
                    </Link>
                )}
            </div>
        );
    }

    return (
        <div className={cn(
            "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6",
            className
        )}>
            {accounts.map((account) => (
                <EnhancedAccountCard
                    key={account._id}
                    account={account}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onView={onView}
                />
            ))}
        </div>
    );
};

// Account Stats Card
export const AccountStatsCard = ({ 
    totalAccounts = 0,
    activeAccounts = 0,
    prospectAccounts = 0,
    avgCompletionRate = 0,
    className = ""
}) => {
    return (
        <div className={cn(
            "bg-card rounded-xl shadow-sm border border-border p-6",
            className
        )}>
            <h3 className="text-lg font-semibold text-foreground mb-4">Account Overview</h3>
            <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                    <div className="text-2xl font-bold text-foreground">{totalAccounts}</div>
                    <div className="text-sm text-muted-foreground">Total Accounts</div>
                </div>
                <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{activeAccounts}</div>
                    <div className="text-sm text-muted-foreground">Active</div>
                </div>
                <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{prospectAccounts}</div>
                    <div className="text-sm text-muted-foreground">Prospects</div>
                </div>
                <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">{avgCompletionRate}%</div>
                    <div className="text-sm text-muted-foreground">Avg Completion</div>
                </div>
            </div>
        </div>
    );
};

export default EnhancedAccountCard;
